/**
 * `KitCopyProvider` fed from the i18n instance (REQUESTS #141 a): the active locale's `Intl` tag and the raw
 * `kitLabelKeys` messages (`i18n.getResource`, never `t`, which would fill the placeholders the kit fills). It
 * follows a language change and a catalogue part with kit words (the store's `added`); a part without kit words
 * keeps the same labels object, so the kit doesn't render again.
 */
import { useCallback, useContext, useSyncExternalStore, type ReactNode } from "react";
import { getI18n, I18nContext } from "react-i18next";
import type { i18n as I18n } from "i18next";
import { KitCopyProvider, type KitLabels } from "@brevalt/ui";
import { kitLabelsFrom } from "./kit-labels";
import { intlLocale, SOURCE_LOCALE } from "./locale";

/** The kit's labels in `lang` (English where its catalogue has none). */
export function kitLabelsOf(i18n: I18n, lang: string): Partial<KitLabels> {
  return kitLabelsFrom(
    (key) =>
      (i18n.getResource(lang, "translation", key) as unknown) ??
      (i18n.getResource(SOURCE_LOCALE, "translation", key) as unknown),
  );
}

interface KitSnapshot {
  lang: string;
  /** The language and the labels' values: the same values keep the same snapshot. */
  key: string;
  labels: Partial<KitLabels>;
}
const snapshots = new WeakMap<I18n, KitSnapshot>();

/** The instance's kit labels now; a new object only when the language or a value changed. */
function snapshotOf(i18n: I18n): KitSnapshot {
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const labels = kitLabelsOf(i18n, lang);
  const key = `${lang}|${JSON.stringify(labels)}`;
  const previous = snapshots.get(i18n);
  if (previous?.key === key) return previous;
  const next = { lang, key, labels };
  snapshots.set(i18n, next);
  return next;
}

function useKitSnapshot(i18n: I18n): KitSnapshot {
  const subscribe = useCallback(
    (changed: () => void) => {
      i18n.on("languageChanged", changed);
      i18n.store.on("added", changed);
      return () => {
        i18n.off("languageChanged", changed);
        i18n.store.off("added", changed);
      };
    },
    [i18n],
  );
  const read = () => snapshotOf(i18n);
  return useSyncExternalStore(subscribe, read, read);
}

export function I18nKitCopy({ children }: { children: ReactNode }) {
  const i18n = (useContext(I18nContext) as { i18n?: I18n } | undefined)?.i18n ?? getI18n();
  const { lang, labels } = useKitSnapshot(i18n);
  return (
    <KitCopyProvider locale={intlLocale(lang)} labels={labels}>
      {children}
    </KitCopyProvider>
  );
}
